'use client';
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { createClient, clearAuthSession, resetRefreshBlock } from '@/lib/supabase/client';

const AuthContext = createContext<any>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  
  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    setMounted(true);
    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
      } catch (e) {
        console.error("Auth Init Error", e);
      } finally {
        setLoading(false);
      }
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Ignore the automatic SIGNED_IN fired right after signUp before OTP confirmation.
      // An unconfirmed user has email_confirmed_at === null.
      if (session?.user && !session.user.email_confirmed_at && event === 'SIGNED_IN') {
        setLoading(false);
        return;
      }
      if (session) setUser(session.user);
      else setUser(null);
      
      if (event === 'SIGNED_OUT') {
        clearAuthSession();
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  const value = {
    user,
    loading,
    supabase,
    signIn: async (email: string, pass: string) => {
      resetRefreshBlock();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password: pass });
      if (error) throw error;
      
      if (data.user) setUser(data.user);

      window.location.href = '/ai-chat-interface';
      
      return data;
    },
    signUp: async (
      email: string,
      pass: string,
      options?: { fullName?: string }
    ) => {
      resetRefreshBlock();
      const { data, error } = await supabase.auth.signUp({
        email,
        password: pass,
        options: {
          data: {
            full_name: options?.fullName ?? '',
          },
        },
      });
      if (error) throw error;

      // Supabase does NOT throw an error for already-registered emails when
      // email confirmation is enabled — it returns a fake user with an empty
      // identities array to prevent email enumeration. Detect this case and
      // throw a recognisable error so the caller can show a proper message
      // without opening the OTP modal or sending any verification code.
      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        const alreadyExistsError: any = new Error('User already registered');
        alreadyExistsError.code = 'user_already_exists';
        throw alreadyExistsError;
      }

      // Do NOT set user here — wait for OTP verification to complete.
      // Setting user immediately would trigger the redirect guard before
      // the OTP modal has a chance to open.
      return data;
    },
    signInWithGoogle: async () => {
      resetRefreshBlock();
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo:
            typeof window !== 'undefined'
              ? `${window.location.origin}/ai-chat-interface`
              : undefined,
        },
      });
      if (error) throw error;
      return data;
    },
    signOut: async () => {
      await supabase.auth.signOut();
      clearAuthSession();
      resetRefreshBlock();
      setUser(null);
      window.location.href = '/sign-up-login-screen';
    }
  };

  if (!mounted) return null; // Stop l'erreur #418

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) return { loading: true }; // Évite le crash "undefined"
  return context;
};